import React from 'react'

import {
  OmButton,
  OmDropdownMenu,
  OmList,
  OmListItem,
  omMessage,
  type OmMenuItem,
  type OmMenuClickInfo,
} from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'
import { getViewText } from 'utils'
import { confirmDelete } from 'utils/ui'
import { truncateSignatureTitle } from 'utils/signatureTitle'

import dataManager from 'pages/main/data/DataManager'
import { renamePopup, openSetDefault, editSignature } from 'popups/ManageSignature'

import settings from 'popups/Settings'

import toolbarSignature from 'assets/images/toolbar-signature.svg'
import toolbarMore24 from 'assets/images/toolbar-more-24.svg'
import menuCheckSVG from 'assets/images/menu-check.svg'

import styles from './style.module.scss'

interface AttachmentFile {
  name: string
  type?: string
  contentId?: string
}

interface SignatureItem {
  signatureId: string
  title: string
  titleKey?: string
  content: string
  html?: string
  attachments?: AttachmentFile[]
  preview?: string[]
  default?: boolean
  editable?: boolean
  origin?: any
}

interface SignatureListProps {
  innerRef?: React.MutableRefObject<any>
}

interface SignatureListState {
  signatureList: SignatureItem[]
}

// 서명 관리 UI는 Settings 팝업 Save(onOk)를 거치지 않고 값을 직접 쓰므로,
// 변경 후 account 카테고리를 명시적으로 서버에 push한다.
// (UPLOAD_SETTINGS saga는 payload의 key 목록만 보고 Redux state를 다시 읽어 전송한다)
const uploadAccountSettings = (): void => {
  settings.getStore().dispatch({
    type: 'settings/UPLOAD_SETTINGS',
    payload: { account: settings.getValue('account') },
  })
}

class SignatureList extends React.Component<SignatureListProps, SignatureListState> {
  state: SignatureListState = {
    signatureList: [],
  }

  componentDidMount(): void {
    const { innerRef } = this.props

    dataManager.signatureAPI?.setReactObj(this)
    this.loadSignatureList()

    if (innerRef != null) {
      innerRef.current = this.createInnerRef()
    }
  }

  componentWillUnmount(): void {
    const { innerRef } = this.props
    dataManager.signatureAPI?.setReactObj(null)

    if (innerRef != null) {
      innerRef.current = null
    }
  }

  createInnerRef = (): any => {
    const obj = {
      createSignature: this.createSignature,
    }

    return obj
  }

  loadSignatureList = async (): Promise<void> => {
    if (dataManager.signatureAPI == null) return

    const originalList: SignatureItem[] = await dataManager.signatureAPI.reloadList(true)

    const list = originalList.map((originalItem) => {
      const item = { ...originalItem }
      item.origin = originalItem
      return item
    })

    const promiseList = list
      .filter((item) => {
        if (item.signatureId === 'default') {
          item.html = item.content
          return false
        }
        return true
      })
      .map((item) => {
        return dataManager.signatureAPI.parseContent(item.content).then((res: any) => {
          if (res != null) {
            const { content, attachments } = res
            item.html = content
            item.attachments = attachments
          } else {
            item.html = item.content
          }
          const text = getViewText(item.html!)
          const preview: string[] = []
          let pos = 0
          let prevPos = 0
          do {
            pos = text.indexOf('\n', pos)
            const line = pos > 0 ? text.substring(prevPos, pos) : ''
            if (line.trim().length > 0) {
              preview.push(line)
            }
            pos += 1
            prevPos = pos
          } while (pos > 0 && preview.length < 2)

          item.preview = preview
          return item
        })
      })

    await Promise.all(promiseList)

    this.setState({
      signatureList: list,
    })
  }

  getTitleStr = (item: SignatureItem): string => {
    const { title, titleKey } = item
    /* i18next-extract-disable-next-line */
    return titleKey != null ? i18n.t(titleKey) : title
  }

  renameSignature = (item: SignatureItem): void => {
    renamePopup({
      title: i18n.t('signature.title.rename'),
      fieldName: null,
      value: this.getTitleStr(item),
      placeholder: i18n.t('signature.placeholder.rename'),
      okText: i18n.t('common.button.save'),
      onOk: (closeFunc: () => void, newName: string) => {
        item.title = newName
        dataManager.signatureAPI
          .updateSignature(item.signatureId, newName, item.content)
          .then(() => {
            this.loadSignatureList()
            if (closeFunc != null) {
              closeFunc()
            }
          })
          .catch((err: Error) => {
            console.log(' -- err : ', err)
            omMessage.error(err.message)
          })
      },
    })
  }

  createSignature = (title: string): void => {
    editSignature({
      isNew: true,
      canImport: true,
      title,
      onOk: (closeFunc: () => void, content: string) => {
        dataManager.signatureAPI
          .createSignature(title, content)
          .then(() => {
            this.loadSignatureList()
            if (closeFunc != null) {
              closeFunc()
            }
          })
          .catch((err: Error) => {
            console.log(' -- err : ', err)
            omMessage.error(err.message)
          })
      },
    })
  }

  getDuplicateTitle = (item: SignatureItem): string => {
    const baseTitle = this.getTitleStr(item)
    const existingTitles = new Set(
      dataManager.getSignatureList().map((signature: any) =>
        /* i18next-extract-disable-next-line */
        signature.titleKey != null ? i18n.t(signature.titleKey) : signature.title,
      ),
    )

    let index = 1
    let title = `${baseTitle} (${index})`
    while (existingTitles.has(title)) {
      index += 1
      title = `${baseTitle} (${index})`
    }

    return title
  }

  innerEditSignature = (item: SignatureItem, key: string): void => {
    let title = key === 'duplicate' ? this.getDuplicateTitle(item) : this.getTitleStr(item)
    if (key === 'duplicate') {
      const { title: finalTitle, truncated } = truncateSignatureTitle(title)
      title = finalTitle
      if (truncated) {
        omMessage.info(i18n.t('signature.duplicateTruncatedNotice'))
      }
    }
    editSignature({
      isNew: key !== 'edit',
      title,
      signature: item,
      onOk: (closeFunc: () => void, content: string) => {
        let promise: Promise<any>
        if (key === 'edit') {
          promise = dataManager.signatureAPI.updateSignature(item.signatureId, title, content)
        } else {
          promise = dataManager.signatureAPI.createSignature(title, content)
        }

        promise
          .then(() => {
            this.loadSignatureList()
            if (closeFunc != null) {
              closeFunc()
            }
          })
          .catch((err: Error) => {
            console.log(' -- err : ', err)
            omMessage.error(err.message)
          })
      },
    })
  }

  deleteSignature = (item: SignatureItem): void => {
    confirmDelete({
      title: i18n.t('signature.title.delete'),
      content: i18n.t('signature.desc.deleteConfirm', { title: this.getTitleStr(item) }),
      width: 380,
      okButtonProps: {
        className: classNames('delete', 'h38'),
      },
      onOk: (closeFunc: () => void): void => {
        dataManager.signatureAPI
          .deleteSignatures([item.signatureId])
          .then(async () => {
            this.loadSignatureList()
            const account = settings.getValue('account') ?? {}
            const updateValues: Record<string, string> = {}
            if (account.signatureForNew === item.signatureId) {
              updateValues['account.signatureForNew'] = 'default'
            }
            if (account.signatureForReplyForward === item.signatureId) {
              updateValues['account.signatureForReplyForward'] = 'none'
            }
            if (Object.keys(updateValues).length > 0) {
              try {
                await settings.setValues(updateValues)
                uploadAccountSettings()
              } catch (err) {
                console.error('Failed to reset signature:', err)
              }
            }
            if (closeFunc != null) {
              closeFunc()
            }
          })
          .catch((err: Error) => {
            console.log(' -- err : ', err)
            omMessage.error(err.message)
          })
      },
    })
  }

  clearDefaultSignature = (item: SignatureItem): void => {
    console.log(' -- clearDefaultSignature : ', item)
    dataManager.signatureAPI
      .setDefaultSignature(item.signatureId, false)
      .then(() => {
        this.loadSignatureList()
      })
      .catch((err: Error) => {
        console.log(' -- err : ', err)
        omMessage.error(err.message)
      })
  }

  setAsDefaultSignature = (item: SignatureItem): void => {
    console.log(' -- setAsDefaultSignature : ', item)
    openSetDefault({
      onOk: (closeFunc: () => void, values: any) => {
        dataManager.signatureAPI
          .setDefaultSignature(item.signatureId, true)
          .then(async () => {
            this.loadSignatureList()
            const updateValues: Record<string, string> = {}
            if (values.newMessage === true) {
              updateValues['account.signatureForNew'] = item.signatureId
            }
            if (values.replyForward === true) {
              updateValues['account.signatureForReplyForward'] = item.signatureId
            }
            if (Object.keys(updateValues).length > 0) {
              await settings.setValues(updateValues)
              uploadAccountSettings()
            }
            if (closeFunc != null) {
              closeFunc()
            }
          })
          .catch((err: Error) => {
            console.log(' -- err : ', err)
            omMessage.error(err.message)
          })
      },
    })
  }

  handleMenuClick = (
    key: string,
    item: SignatureItem,
    domEvent?: OmMenuClickInfo['domEvent'],
  ): void => {
    console.log(' -- handleMenuClick : ', key, item, domEvent)
    switch (key) {
      case 'clearDefault':
        this.clearDefaultSignature(item)
        break
      case 'setAsDefault':
        this.setAsDefaultSignature(item)
        break
      case 'duplicate':
      case 'edit':
        this.innerEditSignature(item, key)
        break
      case 'rename':
        this.renameSignature(item.origin)
        break
      case 'delete':
        this.deleteSignature(item)
        break
      case 'sync':
        this.loadSignatureList()
        break
      case 'view':
        editSignature({
          isNew: false,
          title: this.getTitleStr(item),
          signature: item,
          readonly: true,
        })
        break
      default:
        break
    }
  }

  getMenuItems = (isDefault: boolean, editable: boolean): OmMenuItem[] => {
    let items: OmMenuItem[] = []

    if (editable === true) {
      items = [
        {
          key: isDefault === true ? 'clearDefault' : 'setAsDefault',
          label:
            isDefault === true
              ? i18n.t('signature.menu.clearDefault')
              : i18n.t('signature.menu.setAsDefault'),
        },
        {
          type: 'divider',
        },
        {
          key: 'edit',
          label: i18n.t('signature.menu.edit'),
        },
        {
          key: 'rename',
          label: i18n.t('signature.menu.rename'),
        },
        {
          key: 'duplicate',
          label: i18n.t('signature.menu.duplicate'),
        },
        {
          key: 'delete',
          label: i18n.t('signature.menu.delete'),
        },
        {
          type: 'divider',
        },
        {
          key: 'sync',
          label: i18n.t('signature.menu.sync'),
        },
      ]
    } else {
      items = [
        {
          key: 'view',
          label: i18n.t('signature.menu.view'),
        },
        {
          key: 'duplicate',
          label: i18n.t('signature.menu.duplicate'),
        },
      ]
    }

    return items
  }

  renderItem = (item: SignatureItem): React.ReactElement => {
    const { signatureId, title, titleKey, preview, default: isDefault, editable } = item

    /* i18next-extract-disable-next-line */
    const titleStr = titleKey != null ? i18n.t(titleKey) : title

    let summary: React.ReactElement | React.ReactElement[] | null = null
    if (signatureId === 'default') {
      summary = (
        <div className={classNames(styles.summaryLine)}>
          {i18n.t('signature.desc.defaultSummary')}
        </div>
      )
    } else if (preview != null && preview.length > 0) {
      summary = preview.map((line, index) => {
        const key = `summary-${index}`
        return (
          <div className={classNames(styles.summaryLine)} key={key}>
            {line}
          </div>
        )
      })
    }

    const defaultElem = isDefault === true ? <img src={menuCheckSVG} alt="check" /> : null

    return (
      <OmListItem>
        <div className={classNames(styles.signatureItem)}>
          <div className={classNames(styles.icon)}>
            <img src={toolbarSignature} alt="signature" />
          </div>
          <div className={classNames(styles.content)}>
            <div className={classNames(styles.label)}>
              <span>{titleStr}</span>
              {defaultElem}
            </div>
            <div className={classNames(styles.summary)}>{summary}</div>
          </div>
          <div className={classNames(styles.more)}>
            <OmDropdownMenu
              overlayClassName={classNames('contextMenu')}
              menu={{
                items: this.getMenuItems(isDefault || false, editable || false),
                onClick: ({ key, domEvent }: OmMenuClickInfo) => {
                  console.log(' -- key : ', key, domEvent)
                  this.handleMenuClick(key, item, domEvent)
                },
              }}
              trigger={['click']}
            >
              <OmButton
                className={classNames(styles.moreBtn)}
                variant="text"
                icon={<img src={toolbarMore24} alt="more" />}
              />
            </OmDropdownMenu>
          </div>
        </div>
      </OmListItem>
    )
  }

  render(): React.ReactElement {
    const { signatureList } = this.state

    return (
      <div className={classNames(styles.signatureList, 'signatureList')}>
        <OmList dataSource={signatureList} renderItem={this.renderItem} />
      </div>
    )
  }
}

export default SignatureList
